import { ActivityMessageRenderer } from "@/components/conversations/activity-message-renderer";
import { EntityInteractionThumbnail } from "@/components/conversations/entity-interaction-message-renderer";
import { EntitySharedMessageRenderer } from "@/components/conversations/entity-shared-message-renderer";
import GameMessageRenderer from "@/components/conversations/GameMessageRenderer";
import { TrainingSimulatorTagBadge } from "@/components/conversations/training-simulator-tag-badge";
import type { ChatEntityFeatureFlags } from "@/components/shared/chat-feature-flags-context";
import type {
  ChatMessage,
  ChatRenderContext,
} from "@/components/shared/chat-message-types";
import { resolveTrainingSimulatorTag } from "@/lib/training-simulator/message-tags";
import { parseMessageData } from "@/lib/utils/parse-message-data";
import { ReactNode } from "react";

interface ResolveChatMessageContentArgs {
  message: ChatMessage;
  context?: ChatRenderContext;
  entityFlags: ChatEntityFeatureFlags;
}

export type SpecialChatContent =
  | { kind: "hidden" }
  | { kind: "centered"; content: ReactNode }
  | { kind: "preBubble"; content: ReactNode };

export function resolveChatMessageContent({
  message,
  context,
  entityFlags,
}: ResolveChatMessageContentArgs): SpecialChatContent | null {
  const simulatorTag = resolveTrainingSimulatorTag(message.data);
  if (simulatorTag) {
    return {
      kind: "preBubble",
      content: <TrainingSimulatorTagBadge tag={simulatorTag} />,
    };
  }

  const customType = message.customType;

  if (
    customType === "ENTITY_TAKEDOWN" ||
    customType === "WALL_TAKEDOWN" ||
    customType === "CALL_STARTED" ||
    customType === "CALL_ENDED"
  ) {
    return { kind: "hidden" };
  }

  if (customType === "GAME_START" || customType === "GAME_END") {
    return {
      kind: "centered",
      content: (
        <GameMessageRenderer message={{ customType, data: message.data }} />
      ),
    };
  }

  if (customType === "ACTIVITY") {
    return {
      kind: "centered",
      content: (
        <ActivityMessageRenderer
          message={{ text: message.text, customType, data: message.data }}
          context={context}
        />
      ),
    };
  }

  if (customType === "ENTITY_SHARED") {
    if (
      isEntityFlagGated(
        message.data,
        entityFlags.isMomentsEnabled,
        entityFlags.isSwapsEnabled,
      )
    ) {
      return { kind: "hidden" };
    }
    return {
      kind: "centered",
      content: (
        <EntitySharedMessageRenderer
          message={{ text: message.text, customType, data: message.data }}
          context={context}
        />
      ),
    };
  }

  if (
    customType === "ENTITY_INTERACTION" ||
    customType === "ACTIVITY_INTERACTION"
  ) {
    if (
      isEntityFlagGated(
        message.data,
        entityFlags.isMomentsEnabled,
        entityFlags.isSwapsEnabled,
      )
    ) {
      return { kind: "hidden" };
    }
    return {
      kind: "preBubble",
      content: (
        <EntityInteractionThumbnail
          message={{ customType, data: message.data }}
          context={context}
        />
      ),
    };
  }

  if (message.kind === "admin" && message.text.trim().length > 0) {
    return {
      kind: "centered",
      content: <DefaultAdminMessage text={message.text} />,
    };
  }

  return null;
}

function DefaultAdminMessage({ text }: { text: string }) {
  return (
    <div className="mx-auto max-w-sm rounded-full bg-zinc-50 px-3 py-1.5 text-center text-xs text-zinc-500">
      {text}
    </div>
  );
}

export function isInteractionCustomType(
  customType: string | undefined,
): boolean {
  return (
    customType === "ENTITY_INTERACTION" || customType === "ACTIVITY_INTERACTION"
  );
}

export function isEntityFlagGated(
  data: string | undefined,
  isMomentsEnabled: boolean,
  isSwapsEnabled: boolean,
): boolean {
  const parsed = parseMessageData<{ entity_type?: string }>(data);
  if (!parsed?.entity_type) return false;
  if (!isMomentsEnabled && parsed.entity_type === "moment") return true;
  if (!isSwapsEnabled && parsed.entity_type === "swap") return true;
  return false;
}
